import { createTheme } from '@mui/material/styles'
import miniBookingLogo from '../../../assets/img/Jyrney/yourTMClogo.png'
import loginBackgroundImage from '../../../assets/img/login.jpeg'
import sidebarImage from '../../../assets/img/sidebar-road1.jpg'
import {
    default as loginLogo,
    default as logo,
} from '../../../assets/img/Veezu/Veezu-WhiteCoral.svg'
import hexToRgb from '../../utils/hexToRgb'

/* ==========================================================================
   Veezu — MUI theme
   Aligned with the public veezu.co.uk brand system. Single source of truth
   for the booking/dispatch dashboard + any internal MUI surface.

   Palette is intentionally monochrome: coral is the one accent.
   - Removed: `rose` palette (MUI Dashboard-Pro default, not brand)
   - Removed: `info` #55ACEE blue (Twitter blue, not brand)
   - Removed: gold `secondary.dark` #c08d00 (not brand)
   - Added: warm neutral ramp matching the marketing site
   - Added: `marketing` button variant = pill, sentence case, no uppercase
   ========================================================================== */

// Brand
const veezuBlack = '#000000'
const veezuWhite = '#FFFFFF'
const veezuCoral = '#EC324C'
const veezuCoralHov = '#F26B87'
const veezuCoralDk = '#C01F38'

// Warm neutral ramp (matches colors_and_type.css in the design system)
const n = {
  0: '#FFFFFF',
  50: '#F7F7F7',
  100: '#EDEDED',
  200: '#D1D1D1',
  300: '#B0B0B0',
  400: '#8E8E8E',
  500: '#6B6B6B',
  600: '#4A4A4A',
  700: '#2F2F2F',
  800: '#1A1A1A',
  900: '#000000',
}

// Semantic state colours (coral doubles as error)
const stateSuccess = '#2E7D32'
const stateWarning = '#ED8A19'
const stateDanger = veezuCoral

let theme = createTheme({
  colorBg: '#ffffff',
  brand: {
    name: '',
    logo: logo,
    sidebarImage: sidebarImage,
    miniBookingLogo: miniBookingLogo,
    browserTabName: 'Veezu',
    favicon: 'https://www.veezu.co.uk/favicon.ico',
    mobileUrl: 'https://m.network.veezu.co.uk/',
    coral: veezuCoral,
    coralHover: veezuCoralHov,
    coralDark: veezuCoralDk,
    neutrals: n,
  },
  typography: {
    fontFamily:
      'Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontFamilyDisplay:
      'Poppins, Montserrat, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    htmlFontSize: 16,
    h1: {
      fontFamily: 'Poppins',
      fontWeight: 800,
      fontSize: '4rem',
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontFamily: 'Poppins',
      fontWeight: 700,
      fontSize: '3rem',
      lineHeight: 1.05,
      letterSpacing: '-0.02em',
    },
    h3: {
      fontFamily: 'Poppins',
      fontWeight: 700,
      fontSize: '2.25rem',
      lineHeight: 1.2,
    },
    h4: {
      fontFamily: 'Montserrat',
      fontWeight: 700,
      fontSize: '1.75rem',
      lineHeight: 1.2,
    },
    h5: { fontWeight: 700, fontSize: '1.375rem', lineHeight: 1.2 },
    h6: { fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.2 },
    body1: { fontSize: '1rem', lineHeight: 1.55 },
    body2: { fontSize: '0.875rem', lineHeight: 1.55 },
    overline: {
      fontSize: '0.6875rem',
      fontWeight: 600,
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
    },
    button: {
      fontFamily: 'Poppins',
      fontWeight: 600,
      letterSpacing: 0,
      textTransform: 'none', // default — dashboard button override reintroduces uppercase
    },
  },
  shape: {
    borderRadius: 4,
    borderRadiusPill: 999,
    borderRadiusCard: 12,
    borderRadiusMuiButton: 3,
  },
  map: {
    routeColor: veezuCoral,
    routeColorSatellite: veezuCoral,
  },
  palette: {
    brand: {
      dark: veezuBlack,
      main: veezuBlack,
      light: veezuBlack,
      coral: veezuCoral,
    },
    primary: {
      main: veezuCoral,
      light: veezuCoralHov,
      dark: veezuCoralDk,
      contrastText: veezuWhite,
    },
    secondary: {
      main: veezuBlack,
      light: n[700],
      dark: veezuBlack,
      contrastText: veezuWhite,
    },
    error: {
      main: stateDanger,
      light: veezuCoralHov,
      dark: veezuCoralDk,
      contrastText: veezuWhite,
    },
    warning: {
      main: stateWarning,
      light: '#FFB65A',
      dark: '#B2660E',
      contrastText: veezuWhite,
    },
    success: {
      main: stateSuccess,
      light: '#5EA860',
      dark: '#1B5E20',
      contrastText: veezuWhite,
    },
    info: {
      main: n[700],
      light: n[500],
      dark: n[900],
      contrastText: veezuWhite,
    },
    white: {
      main: veezuWhite,
      light: veezuWhite,
      dark: n[200],
      contrastText: n[900],
    },
    neutral: {
      main: n[500],
      light: n[100],
      dark: n[700],
      contrastText: veezuWhite,
      ...n,
    },
    common: {
      black: veezuBlack,
      white: veezuWhite,
    },
    text: {
      primary: n[900],
      secondary: n[600],
      disabled: n[300],
    },
    background: {
      default: n[0],
      paper: n[0],
      subtle: n[50],
    },
    divider: n[100],
  },
  shadows: [
    'none',
    // 1: card (soft, marketing)
    '0 4px 20px 0 rgba(0,0,0,0.06)',
    // 2: card on image
    '0 8px 24px 0 rgba(0,0,0,0.18)',
    // 3: MUI rest (Dashboard-Pro)
    '0 2px 2px 0 rgba(0,0,0,0.14), 0 3px 1px -2px rgba(0,0,0,0.20), 0 1px 5px 0 rgba(0,0,0,0.12)',
    // 4: MUI coral lift
    '0 14px 26px -12px rgba(236,50,76,0.42), 0 4px 23px 0 rgba(0,0,0,0.12), 0 8px 10px -5px rgba(236,50,76,0.20)',
    // 5: focus ring (coral @ 25%)
    '0 0 0 0.2rem rgba(236,50,76,0.25)',
    // 6-24: fall back to MUI defaults
    ...Array(19).fill('0 2px 4px rgba(0,0,0,0.08)'),
  ],
})

theme = createTheme(theme, {
  pages: {
    loginPage: {
      backgroundImage: 'url(' + loginBackgroundImage + ')',
      titleHeaderInnerColor: theme.palette.primary.main,
      titleHeaderOuterColor: theme.palette.primary.main,
      buttonColor: theme.palette.primary.main,
      loginLogo: loginLogo,
    },
  },
})

theme = createTheme(theme, {
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontFamily: theme.typography.fontFamily,
          color: theme.palette.text.primary,
          backgroundColor: theme.palette.background.default,
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: false },
      styleOverrides: {
        root: ({ ownerState, theme }) => ({
          minHeight: 'auto',
          minWidth: 'auto',
          backgroundColor: theme.palette.common.black,
          color: theme.palette.common.white,
          boxShadow: theme.shadows[3],
          border: 'none',
          borderRadius: theme.shape.borderRadiusMuiButton,
          position: 'relative',
          padding: '9.6px 24px',
          margin: '.3125rem 1px',
          fontFamily: 'Poppins',
          fontSize: '12px',
          fontWeight: 400,
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
          willChange: 'box-shadow, transform',
          transition:
            'box-shadow 0.2s cubic-bezier(0.4,0,1,1), background-color 0.2s cubic-bezier(0.4,0,0.2,1)',
          lineHeight: 1.42857143,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
          touchAction: 'manipulation',
          cursor: 'pointer',
          '&:hover,&:focus': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.common.black,
            boxShadow: theme.shadows[4],
          },
          '& .fab,& .fas,& .far,& .fal,& .material-icons': {
            position: 'relative',
            display: 'inline-block',
            top: 0,
            marginTop: '-1em',
            marginBottom: '-1em',
            fontSize: '1.1rem',
            marginRight: 4,
            verticalAlign: 'middle',
          },
          '& svg': {
            position: 'relative',
            display: 'inline-block',
            top: 0,
            width: 18,
            height: 18,
            marginRight: 4,
            verticalAlign: 'middle',
          },

          /* ---------- Shape modifiers ---------- */
          ...(ownerState?.round && {
            borderRadius: theme.shape.borderRadiusPill,
          }),

          /* ---------- MARKETING PILL (new variant) ---------------------
             Use on public / passenger-facing surfaces. Pill, sentence case.
             <Button marketing color="primary|white|black">…</Button>
             ---------------------------------------------------------- */
          ...(ownerState?.marketing && {
            fontFamily: 'Poppins',
            fontSize: '15px',
            fontWeight: 600,
            textTransform: 'none',
            letterSpacing: 0,
            padding: '14px 22px',
            borderRadius: theme.shape.borderRadiusPill,
            boxShadow: '0 4px 14px rgba(0,0,0,0.12)',
          }),
          ...(ownerState?.marketing &&
            ownerState?.color === 'white' && {
              backgroundColor: theme.palette.common.white,
              color: theme.palette.common.black,
              '&:hover,&:focus': {
                backgroundColor: theme.palette.neutral[50],
                color: theme.palette.common.black,
                boxShadow: '0 6px 18px rgba(0,0,0,0.16)',
              },
            }),
          ...(ownerState?.marketing &&
            ownerState?.color === 'primary' && {
              backgroundColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover,&:focus': {
                backgroundColor: theme.palette.primary.light,
                color: theme.palette.primary.contrastText,
              },
            }),
          ...(ownerState?.marketing &&
            (ownerState?.color === 'black' || !ownerState?.color) && {
              backgroundColor: theme.palette.common.black,
              color: theme.palette.common.white,
              '&:hover,&:focus': {
                backgroundColor: theme.palette.neutral[700],
                color: theme.palette.common.white,
              },
            }),

          /* ---------- Icon-only ---------- */
          ...(ownerState?.justicon && {
            paddingLeft: 12,
            paddingRight: 12,
            fontSize: 20,
            height: 41,
            minWidth: 41,
            width: 41,
            '& .fab,& .fas,& .far,& .fal,& svg,& .material-icons': {
              marginRight: 0,
            },
          }),

          /* ---------- Sizes ---------- */
          ...(ownerState?.size === 'sm' && {
            padding: '0.40625rem 1.25rem',
            fontSize: '0.6875rem',
            lineHeight: 1.5,
          }),
          ...(ownerState?.size === 'lg' && {
            padding: '1.125rem 2.25rem',
            fontSize: '0.875rem',
            lineHeight: 1.333333,
            borderRadius: '0.2rem',
          }),

          /* ---------- Simple (transparent) ---------- */
          ...(ownerState?.simple && {
            '&,&:focus,&:hover': {
              color: theme.palette.common.white,
              background: 'transparent',
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.simple &&
            ownerState?.color && {
              color:
                theme.palette[ownerState?.color]?.main ||
                theme.palette.common.white,
            }),

          /* ---------- Color-tinted solid (non-marketing) ---------- */
          ...(ownerState?.color &&
            !ownerState?.marketing &&
            !ownerState?.simple && {
              boxShadow: theme.shadows[3],
              '&:hover,&:focus': {
                backgroundColor:
                  theme.palette[ownerState?.color]?.main ||
                  theme.palette.common.white,
                boxShadow:
                  ownerState?.color === 'primary' ||
                  ownerState?.color === 'error'
                    ? theme.shadows[4]
                    : theme.shadows[3],
              },
            }),
          ...(ownerState?.color === 'primary' &&
            !ownerState?.marketing && {
              backgroundColor: theme.palette.primary.main,
            }),
          ...(ownerState?.color === 'secondary' &&
            !ownerState?.marketing && {
              backgroundColor: theme.palette.secondary.main,
            }),
          ...(ownerState?.color === 'error' &&
            !ownerState?.marketing && {
              backgroundColor: theme.palette.error.main,
            }),
          ...(ownerState?.color === 'success' &&
            !ownerState?.marketing && {
              backgroundColor: theme.palette.success.main,
            }),
          ...(ownerState?.color === 'warning' &&
            !ownerState?.marketing && {
              backgroundColor: theme.palette.warning.main,
            }),
          ...(ownerState?.color === 'white' &&
            !ownerState?.marketing && {
              backgroundColor: theme.palette.white.main,
              color: theme.palette.neutral[700],
              boxShadow: theme.shadows[3],
              '&,&:focus,&:hover': {
                backgroundColor: theme.palette.white.main,
                color: theme.palette.neutral[700],
              },
            }),

          /* ---------- Transparent / disabled / outlined / text ---------- */
          ...(ownerState?.transparent && {
            '&,&:focus,&:hover': {
              color: 'inherit',
              background: 'transparent',
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.disabled && {
            opacity: 0.65,
            pointerEvents: 'none',
          }),
          ...(ownerState?.outlined && {
            border: '1px solid',
            borderColor: theme.palette.primary.main,
            background: 'transparent',
            color: theme.palette.primary.main,
            boxShadow: 'none',
            '&:focus,&:hover': {
              color: theme.palette.common.white,
              background: theme.palette.primary.main,
              boxShadow: 'none',
            },
          }),
          ...(ownerState?.text && {
            color: theme.palette.primary.main,
            fontWeight: 700,
            backgroundColor: 'transparent',
            border: 'none',
            boxShadow: 'none',
            padding: 5,
            margin: 0,
            '&:focus,&:hover': {
              backgroundColor: 'transparent',
              border: 'none',
              boxShadow: 'none',
            },
            opacity: ownerState.disabled ? 0.5 : 1,
          }),
          ...(ownerState?.variant === 'text' && {
            boxShadow: 'none',
            '&:hover,&:focus': { boxShadow: 'none' },
          }),
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ theme }) => ({
          zIndex: 0,
          backgroundColor: theme.palette.common.white,
          borderRadius: 4,
          position: 'relative',
          border: `1px solid ${theme.palette.neutral[200]}`,
          fontSize: 16,
          transition: 'border-color 0.15s, box-shadow 0.15s',
          '&:focus, &.Mui-focused': {
            boxShadow: theme.shadows[5],
            borderColor: theme.palette.primary.main,
          },
          '&.Mui-error': {
            borderColor: theme.palette.error.main,
          },
        }),
      },
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: ({ theme }) => ({
          color: theme.palette.common.black,
          fontWeight: 600,
          '&:hover': { color: theme.palette.primary.main },
        }),
      },
    },
    MuiChip: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: theme.shape.borderRadiusPill,
          fontWeight: 600,
          fontSize: 13,
        }),
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: 0,
          padding: 0,
          fontFamily:
            theme.typography.fontFamilyDisplay || theme.typography.fontFamily,
          '& .MuiTypography-root': {
            fontFamily:
              theme.typography.fontFamilyDisplay || theme.typography.fontFamily,
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: { displayedRows: { padding: 0, margin: 0 } },
    },
    MuiIcon: {
      styleOverrides: { root: { margin: 0, padding: 0 } },
    },

    /* ---------- App-specific component slots ---------- */
    Sidebar: {
      styleOverrides: {
        overlay: { background: theme.palette.common.black },
      },
    },
    CardHeader: {
      styleOverrides: {
        root: (ownerState, theme) => {
          const color = ownerState?.color || 'white'
          const simple = ownerState?.simple
          const style = {
            background: simple
              ? theme.pages.loginPage.titleHeaderOuterColor
              : `linear-gradient(60deg, ${theme.palette[color]?.dark}, ${theme.palette[color]?.light})`,
          }
          if (color !== 'white') {
            style.boxShadow =
              '0 4px 20px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ',.14), 0 7px 10px -5px rgba(' +
              hexToRgb(theme.pages.loginPage.titleHeaderOuterColor) +
              ',.4)'
          }
          return style
        },
      },
    },
    CardIcon: {
      styleOverrides: {
        root: (ownerState, theme) => {
          const color = ownerState?.color || 'white'
          const simple = ownerState?.simple
          const style = {
            background: simple
              ? theme.palette[color].main
              : `linear-gradient(60deg, ${theme.palette[color]?.dark}, ${theme.palette[color]?.light})`,
          }
          if (color !== 'white') {
            style.boxShadow =
              '0 4px 20px 0 rgba(' +
              hexToRgb(theme.palette.common.black) +
              ',.14), 0 7px 10px -5px rgba(' +
              hexToRgb(theme.palette[color]?.dark) +
              ',.4)'
          }
          return style
        },
      },
    },
    cancelButton: {
      backgroundColor: theme.palette.common.black,
    },
    bookingSummary: {
      backgroundImage: theme.palette.white.main,
      headerColour: theme.palette.common.black,
      headerTextColour: theme.palette.white.main,
      sectionTitle: theme.palette.common.black,
      sectionAnswerPending: theme.palette.neutral[400],
      sectionAnswerComplete: theme.palette.primary.light,
      iconLineComplete: theme.palette.primary.main,
      iconLinePending: theme.palette.neutral[400],
      iconFillComplete: theme.palette.white.main,
      iconFillPending: theme.palette.neutral[400],
    },
    journeyStatus: {
      headerColour: theme.palette.brand.dark,
      headerTextColour: theme.palette.white.main,
    },
    BookingTable: {
      headerColour: theme.palette.brand.light,
    },
  },
  tableComponents: {
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 'bold',
          textAlign: 'left',
          textTransform: 'none',
          padding: 0,
          justifyContent: 'flex-start',
          '&:hover': { background: 'unset' },
        },
      },
    },
  },
})

export default theme
